import type { ComponentProps, ComponentType, ReactNode } from "react";
import { Link } from "react-router";

import type { AreaKey } from "./offering";
import { useAreaHref } from "./use-offering";

type LinkComponent = ComponentType<ComponentProps<typeof Link>>;

/**
 * A link from one area to a page of another area. It is a link only while that area opens for the staff member (their roles allow it, and
 * its feature is on); otherwise it shows `fallback` (plain text, or nothing), never a link that ends in a refusal. `as` is the styled link
 * component of the page (`TextLink`, `BackLink` and so on). A link grants nothing: the page and the API check the role again (ADR 0012).
 */
export function AreaLink({
  area,
  to,
  as: Component = Link,
  fallback = null,
  ...props
}: Omit<ComponentProps<typeof Link>, "to"> &
  Readonly<{ area: AreaKey; to: string; as?: LinkComponent; fallback?: ReactNode }>) {
  const href = useAreaHref()(area, to);
  return href === null ? fallback : <Component to={href} {...props} />;
}
